import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, GuardsCheckEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { filter, merge } from 'rxjs';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { environment } from '~shared/environment';
import { APP_VERSION } from '~shared/version';
import { OpenobserveRumConfig } from './openobserve-rum.config';
import { UserService } from './user.service';
import type { RumActionEventDomainContext, RumBeforeSend } from '@openobserve/browser-rum-slim';

export type OpenobserveRum = typeof import('@openobserve/browser-rum-slim').openobserveRum;

export const OPENOBSERVE_RUM_CONFIG = new InjectionToken<OpenobserveRumConfig>(
  'OPENOBSERVE_RUM_CONFIG',
  { providedIn: 'root', factory: () => environment.rum }
);

// Lazy, so the SDK stays out of the initial bundle.
export const OPENOBSERVE_RUM_LOADER = new InjectionToken<() => Promise<OpenobserveRum>>(
  'OPENOBSERVE_RUM_LOADER',
  {
    providedIn: 'root',
    factory: () => () =>
      import('@openobserve/browser-rum-slim').then(module => module.openobserveRum)
  }
);

// Same id pattern as ROUTE_ID_SEGMENT in ClientErrorService.java.
const ID_SEGMENT =
  /(?<=\/)(\d+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?=[/;]|$)/g;

// The SDK names a click by nearby text, often a person's name, so a click is named by its control alone.
const CONTROL =
  'button, [role="button"], [role="tab"], [role="menuitem"], .p-button, input[type="submit"], input[type="button"]';
const ICON_MODIFIERS = ['pi-fw', 'pi-spin'];

function withoutQuery(url: string): string {
  return url.replace(/[?#].*$/, '');
}

export function maskUrl(url: string): string {
  return withoutQuery(url).replace(ID_SEGMENT, ':id');
}

const cleanEvent: RumBeforeSend = (event, context) => {
  if (event.view?.url) {
    event.view.url = maskUrl(event.view.url);
  }
  if (event.view?.referrer) {
    event.view.referrer = maskUrl(event.view.referrer);
  }
  if (event.type === 'action' && event.action.target) {
    event.action.target.name = controlName(
      (context as RumActionEventDomainContext).events?.[0]?.target
    );
  }
  return true;
};

function controlName(target: EventTarget | null | undefined): string {
  let name = 'other';
  if (target instanceof Element) {
    const control = target.closest(CONTROL);
    if (control) {
      const label = (
        control.getAttribute('aria-label') ||
        control.getAttribute('title') ||
        control.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 100);
      if (label) {
        name = label;
      } else {
        name = iconName(control) ?? name;
      }
    }
  }
  return name;
}

function iconName(control: Element): string | undefined {
  return Array.from(control.querySelector('.pi')?.classList ?? []).find(
    className => className.startsWith('pi-') && !ICON_MODIFIERS.includes(className)
  );
}

// Ported from @openobserve/browser-rum-angular startAngularView.ts.
export function computeViewName(root: ActivatedRouteSnapshot): string {
  const segments: string[] = [];
  let current: ActivatedRouteSnapshot | undefined = root;
  while (current) {
    const path = current.routeConfig?.path;
    if (path === '**') {
      segments.push(...current.url.map(segment => segment.path));
      current = undefined;
    } else {
      if (path) {
        segments.push(path);
      }
      current = current.children.find(child => child.outlet === PRIMARY_OUTLET);
    }
  }
  return `/${segments.join('/')}`;
}

@Injectable({ providedIn: 'root' })
export class OpenobserveRumService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly config = inject(OPENOBSERVE_RUM_CONFIG);
  private readonly load = inject(OPENOBSERVE_RUM_LOADER);
  private rum: OpenobserveRum | undefined;
  private currentPath: string | undefined;
  private pendingView: { name: string; url: string } | undefined;

  start(app: string): void {
    if (this.config.enabled) {
      this.trackNavigations();
      this.trackUserChanges();
      // Not awaited: bootstrap never waits on analytics.
      this.load()
        .then(rum => {
          this.init(rum, app);
          this.rum = rum;
          this.applyUser();
          if (this.pendingView) {
            this.startView(this.pendingView.name, this.pendingView.url);
          }
        })
        .catch(() => undefined);
    }
  }

  sessionId(): string | undefined {
    return this.rum?.getInternalContext()?.session_id;
  }

  private init(rum: OpenobserveRum, app: string): void {
    rum.init({
      applicationId: 'iggoe',
      clientToken: this.config.clientToken,
      site: this.config.site,
      organizationIdentifier: this.config.organizationIdentifier,
      service: app,
      env: this.config.env,
      version: APP_VERSION,
      insecureHTTP: this.config.insecureHTTP,
      trackViewsManually: true,
      trackResources: false,
      enablePrivacyForActionName: false,
      trackAnonymousUser: false,
      telemetrySampleRate: 0,
      beforeSend: cleanEvent
    });
  }

  private trackNavigations(): void {
    this.router.events
      .pipe(
        filter((event): event is GuardsCheckEnd => event instanceof GuardsCheckEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        // List filters change only the query, which is not a new page.
        const path = withoutQuery(event.urlAfterRedirects);
        if (event.shouldActivate && path !== this.currentPath) {
          this.currentPath = path;
          // GuardsCheckEnd comes before the browser URL changes, so the view gets the new URL itself.
          this.startView(
            computeViewName(event.state.root),
            new URL(event.urlAfterRedirects, window.location.origin).href
          );
        }
      });
  }

  private trackUserChanges(): void {
    merge(this.userService.user$, this.authorizationService.currentRole$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyUser());
  }

  private startView(name: string, url: string): void {
    if (this.rum) {
      this.pendingView = undefined;
      this.rum.startView({ name, url });
    } else {
      this.pendingView = { name, url };
    }
  }

  private applyUser(): void {
    const claims = this.authenticationService.authStore.value?.['headers'] ?? {};
    const id = claims['x-hasura-person-id'] ?? claims['x-hasura-user-id'];
    if (id) {
      this.rum?.setUser({ id: String(id), role: this.authorizationService.currentRole() });
    } else {
      this.rum?.clearUser();
    }
  }
}
